import type { Lang } from './text.js';

/**
 * The words of a stop's end (Marek, 2026-10-06), one step at a time on every phone: the vote, where
 * everyone picks the guest they suspect (no skipping, Marek 2026-10-10); the verdict; and the reveal of the stop's saboteurs
 * with what it scored. Plain words, English and bokmål with the same keys.
 */
export interface StopCopy {
  pickEyebrow: (year: string) => string;
  pickTitle: string;
  /** Over the traces of the stop's alarms. */
  pickClues: (alarms: number) => string;
  pickNoClues: string;
  pickPrompt: string;
  /** This phone's vote now: a call sign. */
  pickYours: (name: string) => string;
  pickNotYet: string;
  voted: (n: number, of: number) => string;
  pickOut: string;
  /** The bar, once the sheet is pulled down. */
  pickBar: string;
  pickBarVoted: (name: string | null) => string;

  verdictEyebrow: string;
  verdictOut: (name: string) => string;
  verdictAgent: string;
  verdictNotAgent: string;
  verdictNobody: string;
  verdictNobodyWhy: string;
  verdictYou: string;

  revealEyebrow: string;
  revealTitle: (n: number) => string;
  revealNone: string;
  revealNoneLine: string;
  revealCaught: string;
  revealAway: string;
  revealYou: string;
  yourPoints: string;
  rightVote: string;
  caught: string;
  wronglyOut: string;
  escaped: string;
  damage: (n: number) => string;
  noPoints: string;
  ok: string;
}

const EN: StopCopy = {
  pickEyebrow: (year) => `${year} · THE VOTE`,
  pickTitle: 'WHO IS THE SABOTEUR?',
  pickClues: (alarms) => (alarms === 1 ? 'THE ALARM CAME FROM' : 'THE ALARMS CAME FROM'),
  pickNoClues: 'No alarm went off: go by what you saw.',
  pickPrompt: 'Pick the guest you suspect. You can change it until the captain counts the votes.',
  pickYours: (name) => `Your vote: ${name}`,
  pickNotYet: 'You have not voted yet.',
  voted: (n, of) => `${n} OF ${of} VOTED`,
  pickOut: 'You’re voted out, so you can’t vote this time. Watch the others decide.',
  pickBar: 'Vote now: tap here.',
  pickBarVoted: (name) => (name ? `You voted ${name}. Tap to change.` : 'Vote now: tap here.'),

  verdictEyebrow: 'THE VERDICT',
  verdictOut: (name) => `${name} IS VOTED OUT`,
  verdictAgent: '— A SABOTEUR',
  verdictNotAgent: '— NOT A SABOTEUR',
  verdictNobody: 'NOBODY IS VOTED OUT',
  verdictNobodyWhy: 'A tie, or nobody with two votes.',
  verdictYou: 'That’s you: voted out until the next stop.',

  revealEyebrow: 'THE REVEAL',
  revealTitle: (n) => (n === 1 ? 'THIS STOP’S SABOTEUR' : 'THIS STOP’S SABOTEURS'),
  revealNone: 'NO SABOTEUR THIS STOP',
  revealNoneLine: 'Nobody was drawn: the ship’s own alarms went off.',
  revealCaught: 'CAUGHT',
  revealAway: 'GOT AWAY',
  revealYou: 'YOU',
  yourPoints: 'YOUR POINTS',
  rightVote: 'YOU PICKED A SABOTEUR',
  caught: 'CAUGHT',
  wronglyOut: 'WRONGLY VOTED OUT',
  escaped: 'NOT CAUGHT',
  damage: (n) => (n > 1 ? `DAMAGE DONE ×${n}` : 'DAMAGE DONE'),
  noPoints: 'No points from the vote this time.',
  ok: 'OK',
};

const NO: StopCopy = {
  pickEyebrow: (year) => `${year} · AVSTEMNINGEN`,
  pickTitle: 'HVEM ER SABOTØREN?',
  pickClues: (alarms) => (alarms === 1 ? 'ALARMEN KOM FRA' : 'ALARMENE KOM FRA'),
  pickNoClues: 'Ingen alarm gikk: gå etter det du så.',
  pickPrompt: 'Velg gjesten du mistenker. Du kan endre til kapteinen teller stemmene.',
  pickYours: (name) => `Stemmen din: ${name}`,
  pickNotYet: 'Du har ikke stemt ennå.',
  voted: (n, of) => `${n} AV ${of} HAR STEMT`,
  pickOut: 'Du er stemt ut, så du kan ikke stemme nå. Se de andre bestemme seg.',
  pickBar: 'Stem nå: trykk her.',
  pickBarVoted: (name) => (name ? `Du stemte ${name}. Trykk for å endre.` : 'Stem nå: trykk her.'),

  verdictEyebrow: 'DOMMEN',
  verdictOut: (name) => `${name} ER STEMT UT`,
  verdictAgent: '– EN SABOTØR',
  verdictNotAgent: '– IKKE EN SABOTØR',
  verdictNobody: 'INGEN ER STEMT UT',
  verdictNobodyWhy: 'Uavgjort, eller ingen fikk to stemmer.',
  verdictYou: 'Det er deg: stemt ut til neste stopp.',

  revealEyebrow: 'AVSLØRINGEN',
  revealTitle: (n) => (n === 1 ? 'SABOTØREN PÅ DETTE STOPPET' : 'SABOTØRENE PÅ DETTE STOPPET'),
  revealNone: 'INGEN SABOTØR PÅ DETTE STOPPET',
  revealNoneLine: 'Ingen ble trukket: skipets egne alarmer gikk.',
  revealCaught: 'TATT',
  revealAway: 'SLAPP UNNA',
  revealYou: 'DEG',
  yourPoints: 'POENGENE DINE',
  rightVote: 'DU VALGTE EN SABOTØR',
  caught: 'TATT',
  wronglyOut: 'STEMT UT PÅ URETT',
  escaped: 'IKKE TATT',
  damage: (n) => (n > 1 ? `SKADE GJORT ×${n}` : 'SKADE GJORT'),
  noPoints: 'Ingen poeng fra avstemningen denne gangen.',
  ok: 'OK',
};

export const STOP_COPY: Record<Lang, StopCopy> = { en: EN, no: NO };
